<template>
  <div
    class="max-h-[calc(100vh-40px)] overflow-hidden relative"
    :class="[
      isMarketingHero ? 'marketing-gradient-overlay' : 'gradient-overlay',
      !isLanding ? '' : 'mb-10',
    ]"
  >
    <div
      v-if="heroVideo"
      :class="!isLanding ? 'hero-media' : 'hero-media-landing'"
    >
      <MediaVideo
        v-bind="heroVideo"
        ref="mediaVideo"
        autoplay
        loop
        :controls="false"
        video-aria-hidden
        muted
        :image-style="imageStyle"
        poster=""
        :class="!isMarketingHero && !isLanding ? 'hero-image' : 'w-full'"
        :image="hero"
      />
    </div>
    <MediaImage
      v-else-if="hero"
      v-blokkli-droppable:field_hero_image="{
        entityType: 'media',
        entityBundles: ['image'],
        cardinality: 1,
      }"
      hide-caption
      v-bind="entity?.hero || hero"
      preload
      loading="eager"
      :image-style="imageStyle"
      class=""
      :class="!isMarketingHero && !isLanding ? 'hero-image' : 'w-full'"
    />
    <Container>
      <div
        class="grid-container absolute bottom-6 md:bottom-12 pointer-events-none"
      >
        <h1
          v-if="isLanding || isMarketingHero"
          v-blokkli-editable:title
          class="text-white col-span-4 sm:col-span-6 md:col-span-6 lg:col-span-8 hyphens-none"
          :class="{
            'text-4xl md:text-5xl lg:text-6xl xl:text-7xl': isMarketingHero,
            'text-4xl md:text-5xl lg:text-7xl xl:text-8xl': !isMarketingHero,
          }"
        >
          {{ entity?.title || title }}
        </h1>
        <div
          v-if="lead && isMarketingHero"
          class="col-span-4 sm:col-span-6 md:col-span-6 lg:col-span-8 hyphens-none text-white"
        >
          <div
            v-blokkli-editable:field_lead="{ type: 'frame' }"
            class="text-lg lg:text-xl"
            v-html="lead"
          />
        </div>
        <div
          v-if="heroLink && isMarketingHero"
          class="col-span-4 sm:col-span-6 md:col-span-6 lg:col-span-8 hyphens-none pb-10 text-white"
        >
          <VuepalLink
            :to="heroLink?.uri?.path"
            class="button is-filled pointer-events-auto"
          >
            {{ heroLink.title }}

            <SpriteSymbol name="arrow-right" class="size-6" />
          </VuepalLink>
        </div>
      </div>
    </Container>
  </div>
</template>

<script lang="ts" setup>
import type { NodePageFragment } from '#graphql-operations'

const props = defineProps<{
  hero?: NodePageFragment['hero']
  isMarketingHero?: boolean
  heroLink?: NodePageFragment['heroLink']
  heroVideo?: NodePageFragment['heroVideo']
  title?: string
  lead?: NodePageFragment['lead']
  entity?: any
  isLanding: boolean
}>()

const bigGrid = defineImageStyle({
  type: 'pictures',
  pictures: {
    xs: {
      width: 770,
      aspectRatio: 1,
    },
    sm: {
      width: 985,
      aspectRatio: 16 / 9,
    },
    md: {
      width: 1381,
      aspectRatio: 16 / 9,
    },
    lg: {
      width: 2561,
      aspectRatio: 16 / 9,
    },
  },
})

const marketingGrid = defineImageStyle({
  type: 'pictures',
  pictures: {
    xs: {
      width: 770,
      aspectRatio: 9 / 16,
    },
    sm: {
      width: 985,
      aspectRatio: 16 / 9,
    },
    md: {
      width: 1381,
      aspectRatio: 16 / 9,
    },
    lg: {
      width: 2561,
      aspectRatio: 16 / 9,
    },
  },
})

const smallGrid = defineImageStyle({
  type: 'pictures',
  pictures: {
    xs: {
      width: 728,
      aspectRatio: 1,
    },
    sm: {
      width: 1024,
      aspectRatio: 16 / 6,
    },
    md: {
      width: 1400,
      aspectRatio: 16 / 6,
    },
    lg: {
      width: 2600,
      aspectRatio: 16 / 6,
    },
  },
})

const imageStyle = computed(() => {
  if (props.isMarketingHero) {
    return marketingGrid
  }
  return props.isLanding ? bigGrid : smallGrid
})
</script>

<style lang="postcss">
.hero-image img {
  @apply max-h-[500px] object-cover;
}

.hero-media {
  @apply mx-auto;

  @supports (height: 100svh) {
    /* Use svh unit. 100svh is the smallest possible viewport height. Basically */
    /* the height of the viewport minus any elements that may appear or disappear */
    /* during scrolling (address bar, tab/nav bars, etc.) */
    --small-viewport-height: 100svh;
    /* Since we can use the svh unit, we don't need the fixed browser navbar size anymore. */
    --hero-height-browser-navbar: 0px;
  }

  /* Assume that browsers from this viewport on that don't support svh anyway */
  /* don't display any navbars that overlap 100vh. */
  @screen md {
    --hero-height-browser-navbar: 0px;
  }

  /* The ideal height for the hero so that: */
  /* - it fills the screen top to bottom of the viewport */
  /* - makes sure the anchor nav is always visible */
  --hero-height: calc(
    var(--small-viewport-height, 100vh) - var(--header-height) -
      var(--hero-height-browser-navbar, 80px) - var(--hero-title-height, 0px)
  );

  height: var(--hero-height);
  max-height: 177vw;
  min-height: 500px;
  @screen xs {
    max-height: 80vw;
  }
  @media (max-aspect-ratio: 16/10), (min-width: 2520px) {
  }
  video {
    width: 100%;
    max-width: none;
  }

  picture {
    @apply absolute top-0 left-0 size-full;

    img {
      @apply relative size-full object-cover;
    }
  }

  /* hide all calutter */
  .vjs-controls-disabled .vjs-control-bar,
  .vjs-loading-spinner,
  .vjs-hidden {
    display: none !important;
  }

  .video-js.vjs-fluid {
    height: 0;
    width: 100%;
  }
  .video-js .vjs-tech {
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.hero-media-landing {
  @apply mx-auto;
  @apply h-[calc(100vh-80px)];
}
</style>
